import React from "react";
import { reportError } from "@/lib/api";
import { autoReloadOnStaleChunk } from "@/lib/staleChunkGuard";

// App-wide crash guard. Catches render-time errors, auto-logs them (with a
// screenshot), and shows a branded "Something broke" screen with a one-tap
// "Report this" button so users can flag a broken screen themselves.
// Special-cased: a stale JS chunk reference (dev-server rebuilt while this
// tab was open, then a lazy route link was clicked) auto-recovers with a
// single reload instead of showing the crash screen — see staleChunkGuard.js.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", reporting: false, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Unexpected error" };
  }

  componentDidCatch(error, info) {
    if (autoReloadOnStaleChunk(error)) return;
    // Always auto-log the crash (stack + auto screenshot) so nothing is missed.
    try {
      import("@/lib/rum").then((m) => m.markCrash()).catch(() => {});
      reportError(error?.message || "React render crash", {
        stack: (error?.stack || "") + "\n\nComponent stack:" + (info?.componentStack || ""),
        route: window.location?.pathname,
        category: "crash",
        fatal: true,
        extra: { boundary: true },
      });
    } catch { /* swallow */ }
  }

  handleReport = () => {
    this.setState({ reporting: true });
    try {
      reportError(`User-reported crash: ${this.state.message}`, {
        route: window.location?.pathname,
        category: "crash",
        fatal: true,
        extra: { boundary: true, user_reported: true },
      });
    } catch { /* swallow */ }
    // Give the screenshot capture a beat, then confirm.
    setTimeout(() => this.setState({ reporting: false, reported: true }), 1200);
  };

  handleReload = () => {
    try { window.location.assign("/app"); }
    catch { window.location.reload(); }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="crash-screen"
        style={{ minHeight: "100vh", background: "#050505" }}
        className="flex flex-col items-center justify-center px-6 text-center"
      >
        <div className="max-w-md">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
            <span className="font-display text-3xl text-[#D4AF37]">!</span>
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#F6F5F1]">Something broke</h1>
          <p className="mt-3 text-sm text-[#9C9CA3]">
            This screen hit an unexpected error. It’s not you — the glitch has been noted.
            You can help us fix it faster by reporting exactly what you saw.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3">
            {this.state.reported ? (
              <div
                data-testid="crash-report-thanks"
                className="rounded-full bg-[#3FB950]/15 px-5 py-2.5 text-sm font-bold text-[#7EE787]"
              >
                Thanks — we’re on it ✓
              </div>
            ) : (
              <button
                data-testid="crash-report-btn"
                onClick={this.handleReport}
                disabled={this.state.reporting}
                className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-bold text-[#1A1400] transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {this.state.reporting ? "Sending…" : "Report this"}
              </button>
            )}
            <button
              data-testid="crash-reload-btn"
              onClick={this.handleReload}
              className="rounded-full border border-white/[0.12] px-6 py-3 text-sm font-bold text-[#F6F5F1] transition-colors hover:bg-white/[0.05]"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
